import { debounce } from '@mui/material'
import { CargoType, VesselVisitDto } from '@planning/app/api'
import { vesselVisitService } from '@planning/services'
import { formatDateTime } from '@planning/utils'
import { isVisitStatusBlocked } from '@planning/utils/visit-utils'
import { useTranslate } from '@tolgee/react'
import { TomAutoComplete, TomAutoCompleteProps, VesselIcon } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useState } from 'react'
import { CarrierVisitOption } from '../atoms/CarrierVisitOption'

type Props = {
  selectedVesselVisit?: VesselVisitDto
  handleOnChange?: (value: VesselVisitDto | undefined) => void
  cargoType?: CargoType
  limitBy?: number
  hideBlockedForEdit?: boolean
} & TomAutoCompleteProps<VesselVisitDto>

export const VesselVisitAutoComplete: FC<Props> = observer(props => {
  const {
    selectedVesselVisit,
    label,
    cargoType,
    limitBy = 20,
    sx,
    dataCy,
    handleOnChange,
    hideBlockedForEdit,
  } = props
  const { t } = useTranslate()

  const [options, setOptions] = useState<VesselVisitDto[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  const onSearchFilterChange = debounce(async (filter?: string) => {
    try {
      setLoading(true)
      const options = (await vesselVisitService.get(1, limitBy, filter, cargoType)).payload
      setOptions(
        options.filter(o => {
          return hideBlockedForEdit ? !isVisitStatusBlocked(o.status) : true
        }),
      )
    } finally {
      setLoading(false)
    }
  }, 500)

  const getArrivalDate = (vesselVisit: VesselVisitDto) => {
    if (vesselVisit.ata) return `${t('ata', 'ATA')}: ${formatDateTime(vesselVisit.ata)}`
    else if (vesselVisit.eta) return `${t('eta', 'ETA')}: ${formatDateTime(vesselVisit.eta)}`

    return ''
  }

  return (
    <TomAutoComplete
      {...props}
      sx={sx ?? { mb: '1rem' }}
      label={label ?? t('vesselVisit', 'Vessel visit')}
      entityKey='id'
      getOptionLabel={vesselVisit =>
        `${vesselVisit.identifier} - ${vesselVisit.number} - ${getArrivalDate(vesselVisit)}`
      }
      options={options}
      defaultValue={selectedVesselVisit}
      loading={loading}
      onChangeEntity={entity => {
        if (!entity) {
          setOptions([])
          return
        }

        handleOnChange?.(entity)
      }}
      onChangeFilter={filter => {
        if (!filter) {
          setOptions([])
          handleOnChange?.(undefined)
          return
        }

        onSearchFilterChange?.(filter)
      }}
      startAdornment={<VesselIcon />}
      dataCy={dataCy ?? 'vessel-visit-transport-autocomplete'}
      renderOption={(props, option) => (
        <CarrierVisitOption
          key={`carrier-visit-${option.id}`}
          name={option.identifier!}
          status={option.status}
          isBerthed={option.atb != null}
          date={option.ata ?? option.eta}
          number={option.number}
          renderProps={props}
        />
      )}
    />
  )
})
